import gaiupApolloClient from "@/graphql/services/ApolloClient";
import {
  ReplaceProjectModelsDocument,
  ReplaceProjectModelsWithVersionDocument,
  type ReplaceProjectModelsMutation,
  type ReplaceProjectModelsMutationVariables,
  type ReplaceProjectModelsWithVersionMutation,
  type ReplaceProjectModelsWithVersionMutationVariables,
} from "@/graphql";
import {
  isVersionEntityTypeUnsupported,
  isVersioningSchemaMissing,
} from "@/pages/ProjectVersionHistory/services/versioningSchema";

import { ProjectWriteUnavailableError } from "./projectWriteErrors";
import { modelChangeSummary, modelSignatureChanges, type ProjectModelFlag } from "./modelSignature";

/**
 * A project's models as rows the frontend owns (RTLCA-5629), with or without a 3D model connection.
 * When the project holds a Speckle stream, a row's own trigger enqueues a sync and the API creates or
 * relinks the Speckle model by name — nothing here talks to Speckle.
 *
 * `replaceProjectModels` writes the whole set: the user role has no update permission by design (an
 * UPDATE would not fire the sync triggers), so the working state's rows are deleted and re-inserted
 * with their flags in one document, which Hasura runs as one transaction. Since RTLCA-5745 that is
 * also how a model gets CREATED, and since RTLCA-5756 how one gets REMOVED — every change the Models
 * section makes is an edit held until the page is saved, like every other field there, so this write
 * is the only one left and the trail has to describe all three kinds with it.
 *
 * A removal needs no mutation of its own: the document deletes the whole working set and re-inserts
 * what it is given, so leaving a model out of `models` IS the delete. That is also why nothing here
 * takes a row id any more — the ids the settings page holds came from a read that describes models as
 * Speckle branches, and a single-row delete keyed on one of those matched nothing and reported it as
 * a permissions problem (RTLCA-5756).
 */

export type ProjectModelWriteResult = {
  /** Whether the bundled document carried the trail row, or the plain fallback ran. */
  versionLogged: boolean;
};

/**
 * The trail row for a model save. It names an ADD and a REMOVE as well as a flip, because since
 * RTLCA-5745 the save is also where a model gets created — "Models updated" would otherwise be all the
 * project's history ever said about one. The diff itself lives in `modelSignature.ts`, which this
 * module cannot host: importing it pulls in the Apollo client, and nothing that does can be unit-tested.
 */
const modelsVersionInput = (input: {
  projectId: string;
  models: ProjectModelFlag[];
  previousModels: ProjectModelFlag[] | null;
}) => {
  const changes = input.previousModels === null ? [] : modelSignatureChanges(input.models, input.previousModels);
  const single = changes.length === 1 ? changes[0] : null;
  return {
    projectId: input.projectId,
    entityType: "model",
    operation: "update",
    summary: single ? modelChangeSummary(single) : "Models updated",
    entityLabel: single?.field ?? null,
    changes,
  };
};

/**
 * Write the project's models: every working-state row deleted and re-inserted with its flag, in one
 * transaction. That is how a model is added and turned on or off alike (RTLCA-5745). `previousModels`
 * is the saved baseline, so the trail row can name what moved; null (no baseline) logs a plain
 * "Models updated".
 */
export const replaceProjectModels = async (input: {
  projectId: string;
  models: ProjectModelFlag[];
  previousModels: ProjectModelFlag[] | null;
}): Promise<ProjectModelWriteResult> => {
  const variables = {
    projectId: input.projectId,
    models: input.models.map((model) => ({ projectId: input.projectId, name: model.name, active: model.active })),
  };

  const runPlain = async (): Promise<ProjectModelWriteResult> => {
    const result = await gaiupApolloClient.mutate<ReplaceProjectModelsMutation, ReplaceProjectModelsMutationVariables>({
      mutation: ReplaceProjectModelsDocument,
      variables,
      fetchPolicy: "no-cache",
      context: { tolerateMissingSchema: true },
    });
    if (!result.data?.inserted) throw new Error("The model rows were not returned by Hasura.");
    return { versionLogged: false };
  };

  try {
    const result = await gaiupApolloClient.mutate<
      ReplaceProjectModelsWithVersionMutation,
      ReplaceProjectModelsWithVersionMutationVariables
    >({
      mutation: ReplaceProjectModelsWithVersionDocument,
      variables: { ...variables, version: modelsVersionInput(input) },
      fetchPolicy: "no-cache",
      context: { tolerateMissingSchema: true },
    });
    if (!result.data?.inserted) throw new Error("The model rows were not returned by Hasura.");
    return { versionLogged: true };
  } catch (mutationError) {
    if (!isVersioningSchemaMissing(mutationError) && !isVersionEntityTypeUnsupported(mutationError)) {
      throw mutationError;
    }
    try {
      return await runPlain();
    } catch (fallbackError) {
      if (isVersioningSchemaMissing(fallbackError)) throw new ProjectWriteUnavailableError(fallbackError);
      throw fallbackError;
    }
  }
};
